import React from 'react';
import {useCallback, useState, useMemo} from 'react';
import {Dropdown, Radio} from 'semantic-ui-react';
import * as Hooks from '../../state/hooks';
import {useContext} from 'react';
import {PanelComp2} from './PanelComp';
import {getPanelStacksForType} from './availablePanels';
import * as Panel2 from './panel';
import makeComp from '../../util/profiler';
import {PanelExportContext} from './PanelExportContext';
import {PanelExportUpdaterContext} from './PanelExportContext';
import {envIsDev} from '../../config';
import {ThemeProvider} from 'styled-components';
import * as QueryEditorStyles from './editors/QueryEditor.styles';
import * as Op from './cg/ops';
import * as LLReact from './cg/cgreact';
// import {useTraceUpdate} from '../../util/hooks';

const inputType = {
  type: 'union' as const,
  members: [
    'artifact' as const,
    {type: 'array' as const, objectType: 'artifact' as const},
  ],
};

export interface PanelFilesConfig {
  path?: string[];
}

type FilesProps = Panel2.PanelProps<typeof inputType, PanelFilesConfig>;

export const FilesPanel: React.FC<FilesProps> = makeComp(
  props => {
    const {config, context, updateConfig, updateContext} = props;
    const configPath = config.path || [];
    const pathString = configPath.join('/');

    const {devMode} = useContext(PanelExportContext);
    const {setDevMode} = useContext(PanelExportUpdaterContext);

    const newPanelContext = useMemo(
      () => ({
        ...context,
        path: configPath,
      }),
      [context, configPath]
    );

    const artifacts = props.input.path;

    const files = useMemo(() => {
      return Op.applyOpToOneOrMany(Op.opArtifactFile, 'artifact', artifacts, {
        path: Op.constString(pathString),
      });
    }, [artifacts, pathString]);
    const fileWithTypeRaw = LLReact.useNodeWithServerType(files);
    const fileWithType = Hooks.useGatedValue(
      fileWithTypeRaw,
      ft => !ft.loading
    );

    // We currently just store configs of our previews here, keyed by previewer
    // type. This is probably not really right. Maybe use file path?
    // TODO: better config strategy
    const [subConfigs, setSubConfigs] = useState<{[key: string]: any}>({});

    const [curPanelIdState, setCurPanelId] = useState<string | undefined>(
      undefined
    );

    const panelInputType = fileWithType.result.type;
    const {curPanelId, stackIds, handler} = useMemo(
      () => getPanelStacksForType(panelInputType, curPanelIdState),
      [panelInputType, curPanelIdState]
    );
    const finalInput = useMemo(() => {
      return {path: fileWithType.result};
    }, [fileWithType.result]);

    const panelConfig = useMemo(
      () =>
        curPanelId != null ? subConfigs[curPanelId + '-' + pathString] : {},
      [curPanelId, pathString, subConfigs]
    );

    const panelUpdateConfig = useCallback(
      (newConfig: any) => {
        if (curPanelId != null) {
          setSubConfigs(curSubConfigs => ({
            ...curSubConfigs,
            [curPanelId! + '-' + pathString]: {
              ...curSubConfigs[curPanelId! + '-' + pathString],
              ...newConfig,
            },
          }));
        }
      },
      [curPanelId, pathString]
    );
    const panelUpdateContext = useCallback(
      (newContext: any) => {
        if (newContext.path != null) {
          updateConfig({path: newContext.path});
        }
        updateContext(newContext);
      },
      [updateConfig, updateContext]
    );

    return (
      <ThemeProvider theme={QueryEditorStyles.themes.carey}>
        <div
          className="file-browser artifact-file-browser"
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}>
          <div
            className="file-browser-path"
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 16,
              position: 'sticky',
              left: '0px',
              zIndex: 1001,
            }}>
            <div style={{fontSize: 24, opacity: 0.7}}>
              &gt;&nbsp;
              {['root'].concat(configPath).map((folderName, i) => {
                const newPath = configPath.slice(0, i);
                return [
                  <span
                    className="file-browser-path-item"
                    style={{cursor: 'pointer'}}
                    key={'path' + i}
                    onClick={e => updateConfig({path: newPath})}>
                    {folderName}
                  </span>,
                  i !== configPath.length ? ' / ' : undefined,
                ];
              })}
            </div>

            {stackIds.length > 1 && (
              <Dropdown
                selection
                className="clone-report-modal__select"
                style={{marginLeft: 24, zIndex: 1001}}
                options={stackIds.map(si => ({
                  key: si.id,
                  text: si.displayName,
                  value: si.id,
                }))}
                value={curPanelId}
                onChange={(e, {value}) => setCurPanelId(value as string)}
              />
            )}
          </div>
          <div
            className="file-browser-path"
            style={{
              marginBottom: 16,
              // position: 'sticky',
              // left: '0px',
              // zIndex: 1001,
            }}>
            {envIsDev && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 16,
                  }}>
                  Dev mode{' '}
                  <Radio
                    style={{marginLeft: 12}}
                    toggle
                    checked={devMode}
                    onClick={() => setDevMode(!devMode)}
                  />
                </div>
              </>
            )}
          </div>
          {handler != null ? (
            <>
              {/* If the panel has config UI, render it on top. If it doesn't,
            this won't show anything */}
              <PanelComp2
                input={finalInput as any}
                inputType={panelInputType}
                loading={false}
                panelSpec={handler}
                configMode={true}
                context={newPanelContext}
                config={panelConfig || {}}
                updateConfig={panelUpdateConfig}
                updateContext={panelUpdateContext}
              />
              {/* Then render it normally. */}
              <PanelComp2
                input={finalInput as any}
                inputType={panelInputType}
                loading={false}
                panelSpec={handler}
                configMode={false}
                context={newPanelContext}
                config={panelConfig || {}}
                updateConfig={panelUpdateConfig}
                updateContext={panelUpdateContext}
              />
            </>
          ) : (
            <div>no preview</div>
          )}
        </div>
      </ThemeProvider>
    );
  },
  {id: 'FilesPanel'}
);
